/** @format */

import { useState, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useLazyQuery } from '@apollo/client';
import { RootState } from 'state/store';
import { SEARCH_CUSTOMER, SearchCustomerItem } from '../services/searchCustomer';

interface UseSearchCustomersReturn {
  customers: SearchCustomerItem[];
  loading: boolean;
  error: string | null;
  search: (query: string) => void;
}

/**
 * Hook to search customers with role-based office filtering
 * 
 * - root_user or root_custo: No office filter (searches all)
 * - admin or custo: Filters by user's officeId
 * - Users with downlineAccess: Filters by array of downline office IDs
 */
export const useSearchCustomers = (): UseSearchCustomersReturn => {
  const [customers, setCustomers] = useState<SearchCustomerItem[]>([]);
  const [error, setError] = useState<string | null>(null);

  // Get user data from Redux
  const userData = useSelector((state: RootState) => (state.user as any).userData);
  const userRole = userData?.role;
  const userOfficeId = userData?.officeId;
  const hasDownlineAccess = userData?.userAccess?.downlineAccess === true;
  const officeDownLines = userData?.officeDownLines;

  // Determine office IDs based on user role
  const getOfficeIds = useCallback((): string[] | undefined => {
    const isRootUser = userRole && ['root_user', 'root_custo'].includes(userRole);
    const downlines = officeDownLines?.downlines || [];

    if (isRootUser) {
      // Root users can search all - no office filter
      return undefined;
    }

    if (hasDownlineAccess && downlines.length > 0) {
      // Users with downline access - pass array of downline office IDs
      return downlines.map((d: { officeId: string }) => d.officeId);
    }

    if (userRole === 'admin' || userRole === 'custo') {
      // Admin or custo - pass user's own office ID
      return userOfficeId ? [userOfficeId] : undefined;
    }

    // Default - pass user's office ID
    return userOfficeId ? [userOfficeId] : undefined;
  }, [userRole, userOfficeId, hasDownlineAccess, officeDownLines]);

  const [searchCustomers, { loading }] = useLazyQuery(SEARCH_CUSTOMER, {
    fetchPolicy: 'network-only',
    onCompleted: (data) => {
      if (data?.searchCustomersLegacy) {
        setCustomers(data.searchCustomersLegacy);
        setError(null);
      } else {
        setCustomers([]);
      }
    },
    onError: (err) => {
      console.error('Error searching customers:', err);
      setError('Failed to search customers');
      setCustomers([]);
    },
  });

  const search = useCallback(
    (query: string) => {
      if (!query.trim()) {
        setError('Please enter a search query');
        return;
      }

      const officeIds = getOfficeIds();

      searchCustomers({
        variables: {
          query: query.trim(),
          officeIds: officeIds,
        },
      });
    },
    [searchCustomers, getOfficeIds]
  );

  return {
    customers,
    loading,
    error,
    search,
  };
};
